import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { SavedBuyPlan, SavedSellPlan, SavedPlanTier } from '../../types/savedPlans';
import { useSavedBuyPlans, useSavedSellPlans } from '../../hooks/useSavedPlans';
import { CoinIcon } from '../Transactions/CoinIcon';
import { loadCoinImages } from '../../utils/coinImageCache';
import { formatMoney, formatExactPrice, formatDateShort } from '../../utils/formatters';

const BRONZE = '#b89968';
const GREEN = '#6db981';

const eyebrowStyle: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: BRONZE,
  fontWeight: 600,
};

// Tier-price list at the EXACT precision the user entered, e.g.
// "$1.112 / $1.00 / $0.85" — never magnitude-bucketed (that caused "$1 / $1 / $1").
function tierPriceList(tiers: SavedPlanTier[], currency: string): string {
  return tiers
    .filter((t) => t.price > 0)
    .map((t) => formatExactPrice(t.price, currency))
    .join(' / ');
}

function PlanRow({
  coinId,
  coinTicker,
  coinImage,
  coinName,
  planName,
  tierCount,
  tiers,
  currency,
  amount,
  savedAt,
  imageMap,
  active,
  highlighted,
  onSelect,
  onDelete,
}: {
  coinId: string;
  coinTicker: string;
  coinImage?: string;
  coinName: string;
  planName: string;
  tierCount: number;
  tiers: SavedPlanTier[];
  currency: string;
  amount: number;
  savedAt: string;
  imageMap: Record<string, string>;
  active: boolean;
  highlighted?: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={`group flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors cursor-pointer${highlighted ? ' plan-saved-flash' : ''}`}
      onClick={onSelect}
      title="Click to edit this plan"
      style={{
        background: active ? 'rgba(212,180,131,0.10)' : 'transparent',
        borderLeft: active ? '2px solid #d4b483' : '2px solid transparent',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgb(var(--overlay) / 0.04)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <CoinIcon coinId={coinId} symbol={coinTicker} imageMap={{ ...imageMap, ...(coinImage ? { [coinId]: coinImage } : {}) }} size={22} />

      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium truncate" style={{ color: 'rgb(var(--sl-100))' }}>
          {coinName}
          {planName && (
            <span className="ml-1.5" style={{ color: BRONZE }}>· {planName}</span>
          )}
        </p>
        <p className="text-[10px] truncate mt-0.5" style={{ color: 'var(--color-sub)' }}>
          {tierCount} tier{tierCount === 1 ? '' : 's'}
          {tierPriceList(tiers, currency) ? ` · ${tierPriceList(tiers, currency)}` : ''}
        </p>
      </div>

      <span className="text-[12px] num font-semibold flex-shrink-0" style={{ color: GREEN }}>
        {formatMoney(amount, currency)}
      </span>

      <span className="text-[10px] flex-shrink-0 hidden sm:inline" style={{ color: 'var(--color-sub)' }}>
        {formatDateShort(savedAt)}
      </span>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title="Delete plan"
        className="p-1 rounded-md flex-shrink-0 transition-colors opacity-60 group-hover:opacity-100"
        style={{ color: 'var(--color-sub)' }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-negative)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-sub)')}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function BandShell({
  eyebrow,
  totalLabel,
  totalText,
  empty,
  children,
}: {
  eyebrow: string;
  totalLabel: string;
  totalText: string;
  empty: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="mb-3.5"
      style={{
        background: 'rgb(var(--overlay) / 0.02)',
        border: '0.5px solid rgb(var(--overlay) / 0.05)',
        borderRadius: 10,
        padding: '14px 16px',
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <span style={eyebrowStyle}>{eyebrow}</span>
        {!empty && (
          <span className="text-[12px]" style={{ color: 'var(--color-sub)' }}>
            {totalLabel}{' '}
            <span className="num font-bold" style={{ color: GREEN }}>{totalText}</span>
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export function SavedBuyPlansBand({ activeId, highlightId, onSelect }: {
  activeId?: string | null;
  highlightId?: string | null;
  onSelect?: (plan: SavedBuyPlan) => void;
}) {
  const { plans, deletePlan, getTotal } = useSavedBuyPlans();
  const baseImageMap = useMemo(() => loadCoinImages(), []);
  const currency = plans[0]?.currency ?? 'USD';
  const total = getTotal();

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this plan?')) deletePlan(id);
  };

  // Empty state is surfaced as a muted count next to the sub-tab (StrategyView),
  // so render nothing here until there's at least one saved plan.
  if (plans.length === 0) return null;

  return (
    <BandShell
      eyebrow={`Saved buy plans · ${plans.length}`}
      totalLabel="Total to deploy:"
      totalText={formatMoney(total, currency)}
      empty={false}
    >
      <div className="flex flex-col gap-0.5">
        {plans.map((p: SavedBuyPlan) => (
          <PlanRow
            key={p.id}
            coinId={p.coinId}
            coinTicker={p.coinTicker}
            coinImage={p.coinImage}
            coinName={p.coinName}
            planName={p.planName}
            tierCount={p.tierCount}
            tiers={p.tiers}
            currency={p.currency}
            amount={p.totalBudget}
            savedAt={p.savedAt}
            imageMap={baseImageMap}
            active={p.id === activeId}
            highlighted={p.id === highlightId}
            onSelect={() => onSelect?.(p)}
            onDelete={() => handleDelete(p.id)}
          />
        ))}
      </div>
    </BandShell>
  );
}

export function SavedSellPlansBand({ activeId, highlightId, onSelect }: {
  activeId?: string | null;
  highlightId?: string | null;
  onSelect?: (plan: SavedSellPlan) => void;
}) {
  const { plans, deletePlan, getTotal } = useSavedSellPlans();
  const baseImageMap = useMemo(() => loadCoinImages(), []);
  const currency = plans[0]?.currency ?? 'USD';
  const total = getTotal();

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this plan?')) deletePlan(id);
  };

  // Empty state is surfaced as a muted count next to the sub-tab (StrategyView),
  // so render nothing here until there's at least one saved plan.
  if (plans.length === 0) return null;

  return (
    <BandShell
      eyebrow={`Saved sell plans · ${plans.length}`}
      totalLabel="Total expected proceeds:"
      totalText={formatMoney(total, currency)}
      empty={false}
    >
      <div className="flex flex-col gap-0.5">
        {plans.map((p: SavedSellPlan) => (
          <PlanRow
            key={p.id}
            coinId={p.coinId}
            coinTicker={p.coinTicker}
            coinImage={p.coinImage}
            coinName={p.coinName}
            planName={p.planName}
            tierCount={p.tierCount}
            tiers={p.tiers}
            currency={p.currency}
            amount={p.expectedProceeds}
            savedAt={p.savedAt}
            imageMap={baseImageMap}
            active={p.id === activeId}
            highlighted={p.id === highlightId}
            onSelect={() => onSelect?.(p)}
            onDelete={() => handleDelete(p.id)}
          />
        ))}
      </div>
    </BandShell>
  );
}
